import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { DotScreenShader } from '../components/DotScreenShader';
import { Footer } from '../components/Footer';


export const PUBLIC_TIER_OPTIONS = [
    {
        key: 'free',
        label: 'Free',
        amountDisplay: '$0',
        priceSuffix: '/month',
        limitDescription: '50 monthly credits · 500MB storage',
        description: 'Perfect for testing the workflow.',
        badge: 'Free forever',
        features: ['50 credits/mo', '500MB Storage', '7-day retention'],
    },
    {
        key: 'creator',
        label: 'Creator',
        amountDisplay: '$9.99',
        priceSuffix: '/month',
        limitDescription: '1,100 monthly credits · 5GB storage',
        description: 'For hobbyists and content creators.',
        badge: 'Popular',
        features: ['1,100 credits/mo', '5GB Storage', '30-day retention'],
        featured: true,
    },
    {
        key: 'pro',
        label: 'Pro',
        amountDisplay: '$24.99',
        priceSuffix: '/month',
        limitDescription: '3,000 monthly credits · 25GB storage',
        description: 'For power users and professionals.',
        badge: 'Power User',
        features: ['3,000 credits/mo', '25GB Storage', '90-day retention'],
    },
    {
        key: 'studio',
        label: 'Studio',
        amountDisplay: '$49.99',
        priceSuffix: '/month',
        limitDescription: '6,500 monthly credits · 100GB storage',
        description: 'Maximum capacity for serious production.',
        badge: 'Agency',
        features: ['6,500 credits/mo', '100GB Storage', 'Unlimited retention'],
    },
];

export const SignUpPage = ({
    onAuthSubmit,
    onOAuth,
    oAuthLoading,
    authLoading,
    authError,
    authEmail,
    setAuthEmail,
    authPassword,
    setAuthPassword,
    selectedSignupTier,
    setSelectedSignupTier,
    billingInterval,
    setBillingInterval
}) => {

    const [showSignupForm, setShowSignupForm] = useState(false);
    const selectedTierMeta = PUBLIC_TIER_OPTIONS.find((tier) => tier.key === selectedSignupTier) || PUBLIC_TIER_OPTIONS[0];

    const handleSubmit = async (e) => {
        e.preventDefault();
        await onAuthSubmit(e, 'sign_up');
        // Navigation handled by auth flow (may redirect to Stripe)
    };

    return (
        <div className="App" style={{ position: 'relative' }}>
            <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
                <DotScreenShader theme="dark" />
            </div>

            <Navbar />

            <main className="hero hero--auth hero--auth-sign_up" style={{ position: 'relative', zIndex: 1, paddingTop: '100px' }}>
                <div className="auth-page">
                    {!showSignupForm ? (
                        <section className="signup-card">
                            <p className="hero-preheader hero-preheader--centered">#1 AI FACELESS VIDEO TOOL</p>
                            <h1 className="hero-heading hero-heading--centered">Choose a Plan</h1>
                            <p className="hero-subheader">Cancel Anytime</p>
                            <div className="billing-toggle-container">
                                <span className={`toggle-label ${billingInterval === 'month' ? 'active' : ''}`}>Monthly</span>
                                <button
                                    type="button"
                                    className={`toggle-switch ${billingInterval === 'year' ? 'checked' : ''}`}
                                    onClick={() => setBillingInterval(prev => prev === 'month' ? 'year' : 'month')}
                                >
                                    <span className="toggle-thumb" />
                                </button>
                                <span className={`toggle-label ${billingInterval === 'year' ? 'active' : ''}`}>
                                    Annual <span className="discount-badge">Save 20%</span>
                                </span>
                            </div>
                            <div className="signup-tier-grid">
                                {PUBLIC_TIER_OPTIONS.map((tier) => {
                                    let displayAmount = tier.amountDisplay;
                                    let suffix = tier.priceSuffix || '/month';
                                    if (billingInterval === 'year' && tier.amountDisplay !== '$0') {
                                        const val = parseFloat(tier.amountDisplay.replace('$', ''));
                                        if (!isNaN(val)) {
                                            displayAmount = `$${(val * 0.8).toFixed(2)}`;
                                            suffix = '/month';
                                        }
                                    }
                                    return (
                                        <label
                                            key={tier.key}
                                            className={`signup-tier-card ${selectedSignupTier === tier.key ? 'signup-tier-card--active' : ''}`}
                                            onClick={() => { setSelectedSignupTier(tier.key); setShowSignupForm(true); }}
                                        >
                                            <input
                                                type="radio"
                                                name="signup-tier"
                                                value={tier.key}
                                                checked={selectedSignupTier === tier.key}
                                                onChange={() => setSelectedSignupTier(tier.key)}
                                            />
                                            <div className="signup-tier-card__content">
                                                <div className="signup-tier-card__badge-row">
                                                    <span className={`signup-tier-card__pill ${tier.key === 'pro' ? 'signup-tier-card__pill--highlight' : ''}`}>{tier.label}</span>
                                                </div>
                                                <div className="signup-tier-card__price-row">
                                                    <span className="signup-tier-card__price">{displayAmount}</span>
                                                    <span className="signup-tier-card__frequency">{suffix}</span>
                                                </div>
                                                <p className="signup-tier-card__limit">{tier.limitDescription}</p>
                                                <span className="signup-tier-card__cta">Choose {tier.label}</span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>

                            {/* Credit Usage Comparison Table */}
                            <div className="signup-tier-grid" style={{ marginTop: '50px', display: 'block' }}>
                                <h3 style={{ marginBottom: '20px', fontSize: '1.3rem', textAlign: 'center' }}>Monthly Credit Usage by Tool</h3>
                                <div style={{
                                    overflowX: 'auto',
                                    background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.04) 100%)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    borderRadius: '16px',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)'
                                }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '0.9rem',
                                        minWidth: '600px'
                                    }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                                                <th style={{ textAlign: 'left', padding: '15px', color: '#888', fontWeight: '500' }}>Tool</th>
                                                <th style={{ textAlign: 'center', padding: '15px', color: '#888', fontWeight: '500' }}>Credits</th>
                                                <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Free<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>50/mo</span></th>
                                                <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Creator<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>1,100/mo</span></th>
                                                <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Pro<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>3,000/mo</span></th>
                                                <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Studio<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>6,500/mo</span></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { tool: 'Rant Video', credits: 30, free: 1, creator: 36, pro: 100, studio: 216 },
                                                { tool: 'Quiz Video', credits: 10, free: 5, creator: 110, pro: 300, studio: 650 },
                                                { tool: 'Ranked Export', credits: 5, free: 10, creator: 220, pro: 600, studio: 1300 },
                                                { tool: 'Smart Clips', credits: 2, free: '25 min', creator: '550 min', pro: '1,500 min', studio: '3,250 min' },
                                                { tool: 'Downloader', credits: 0, free: '∞', creator: '∞', pro: '∞', studio: '∞' },
                                                { tool: 'Audio Converter', credits: 0, free: '∞', creator: '∞', pro: '∞', studio: '∞' },
                                                { tool: 'Video Splitter', credits: 0, free: '∞', creator: '∞', pro: '∞', studio: '∞' },
                                            ].map((row, idx) => (
                                                <tr key={idx} style={{
                                                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                                                    background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                                                }}>
                                                    <td style={{ textAlign: 'left', padding: '14px 15px' }}>{row.tool}</td>
                                                    <td style={{ textAlign: 'center', padding: '14px 15px', color: row.credits === 0 ? '#10b981' : '#888' }}>
                                                        {row.credits === 0 ? 'Free' : row.credits}
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff' }}>{row.free}</td>
                                                    <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff' }}>{row.creator}</td>
                                                    <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff' }}>{row.pro}</td>
                                                    <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff', fontWeight: '500' }}>{row.studio}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div style={{ padding: '15px', color: '#666', fontSize: '0.8rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                        <strong>Note:</strong> Rant = Script (5) + Voice (20) + Render (5) = 30 credits · Quiz = Generate (5) + Render (5) = 10 credits · Smart Clips charged per minute of source video
                                    </div>
                                </div>
                            </div>
                        </section>
                    ) : (
                        <section className="signup-card">
                            <div className="signup-form-card">
                                <button
                                    type="button"
                                    className="auth-page__back"
                                    onClick={() => setShowSignupForm(false)}
                                    style={{ marginBottom: '16px' }}
                                >
                                    ← Back to plans
                                </button>
                                <div className="signup-tier-summary">
                                    <div className="signup-tier-summary__name">
                                        {selectedTierMeta.label}
                                    </div>
                                    <div className="signup-tier-summary__price">
                                        <span>{selectedTierMeta.amountDisplay}</span>
                                    </div>
                                </div>
                                <button type="button" className="oauth-btn" onClick={() => onOAuth(true)} disabled={oAuthLoading}>
                                    {oAuthLoading ? (
                                        <>
                                            <span className="oauth-spinner"></span>
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="oauth-icon" viewBox="0 0 48 48" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                                                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                                <path fill="none" d="M0 0h48v48H0z" />
                                            </svg>
                                            Continue with Google
                                        </>
                                    )}
                                </button>

                                <div className="auth-divider">or continue with email</div>

                                <form onSubmit={handleSubmit}>
                                    <input
                                        id="signup-email"
                                        type="email"
                                        required
                                        value={authEmail}
                                        onChange={(e) => setAuthEmail(e.target.value)}
                                        placeholder="Enter email address"
                                    />
                                    <input
                                        id="signup-password"
                                        type="password"
                                        required
                                        value={authPassword}
                                        onChange={(e) => setAuthPassword(e.target.value)}
                                        minLength={6}
                                        placeholder="Create password"
                                    />
                                    <button type="submit" disabled={authLoading}>
                                        {authLoading ? 'Submitting…' : 'Create account'}
                                    </button>
                                </form>

                                <p className="auth-toggle">
                                    Already have an account? <Link to="/signin">Sign in</Link>
                                </p>
                                {authError && <p className="error">{authError}</p>}
                            </div>
                        </section>
                    )}
                </div>
            </main>

            <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
                <Footer />
            </div>
        </div>
    );
};
